import type { HttpError } from "@vita-learning/types";

export type HttpMethod = "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Type literal for standard HTTP request header keys with capitalized words
 */
type HttpRequestHeaderKeys =
	| "Accept"
	| "Accept-Charset"
	| "Accept-Encoding"
	| "Accept-Language"
	| "Authorization"
	| "Cache-Control"
	| "Connection"
	| "Content-Encoding"
	| "Content-Length"
	| "Content-Type"
	| "Cookie"
	| "Date"
	| "Expect"
	| "Host"
	| "If-Match"
	| "If-Modified-Since"
	| "If-None-Match"
	| "If-Range"
	| "If-Unmodified-Since"
	| "Max-Forwards"
	| "Origin"
	| "Pragma"
	| "Proxy-Authorization"
	| "Range"
	| "Referer"
	| "TE"
	| "User-Agent"
	| "Upgrade"
	| "Via"
	| "Warning"
	| "DNT"
	| "X-Requested-With"
	| "X-Forwarded-For"
	| "X-Forwarded-Host"
	| "X-Forwarded-Proto"
	| "Front-End-Https"
	| "X-Http-Method-Override"
	| "X-ATT-DeviceId"
	| "X-Wap-Profile";

/**
 * Record type for standard HTTP request headers with the flexibility to use custom keys.
 */
export type HttpHeaders = {
	[Key in HttpRequestHeaderKeys]?: string;
} & {
	[customKey: string]: string;
};

export interface HttpConfig
	extends Omit<RequestInit, "body" | "method" | "headers"> {
	/**
	 * Query params as an object
	 *
	 * HttpClient will automatically format the query string with the key-value pairs
	 */
	readonly params?: Record<string, unknown>;

	/**
	 * HTTP request headers
	 */
	readonly headers?: HttpHeaders;

	/**
	 *  HTTP request body
	 */
	readonly data?: unknown;
	/**
	 * Request timeout in ms
	 */
	readonly timeout?: number;
	/**
	 * Amount of times to retry request upon failure
	 */
	readonly retries?: number;
	/**
	 * Callback for handling streaming response chunks
	 */
	readonly onStream?: (chunk: string) => void;

	/**
	 * If true, throws error on non-2xx responses. If false, returns response data as-is.
	 * @default false
	 */
	readonly throwOnError?: boolean;
}

const DEFAULT_TIMEOUT = 10_000;

interface HttpClientDefaults {
	/**
	 * Base URL for all requests
	 */
	readonly baseURL?: string;
	/**
	 * Request timeout in ms
	 */
	readonly timeout: number;
	/**
	 * Request headers
	 */
	readonly headers: HttpHeaders;

	/**
	 * If true, throws error on non-2xx responses. If false, returns response data as-is.
	 * @default false
	 */
	readonly throwOnError?: boolean;
}

export class HttpClient {
	readonly #defaults: HttpClientDefaults;

	constructor(
		baseURL?: string,
		{
			timeout = DEFAULT_TIMEOUT,
			headers = {},
			throwOnError = false,
		}: Partial<HttpClientDefaults> = {
			timeout: DEFAULT_TIMEOUT,
			headers: {},
			throwOnError: false,
		}
	) {
		this.#defaults = {
			baseURL,
			timeout,
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...headers,
			},
			throwOnError,
		};
	}

	#formatUrl(path: string | URL, params?: Record<string, unknown>) {
		if (path instanceof URL) {
			if (params && Object.keys(params).length > 0)
				Object.entries(params).forEach(
					([k, v]) =>
						v && path.searchParams.set(k, encodeURIComponent(JSON.stringify(v)))
				);

			return path;
		}

		const url = (this.getDefaults().baseURL ?? "") + path;

		if (params && Object.keys(params).length > 0) {
			const searchParams = new URLSearchParams();

			Object.entries(params).forEach(
				([k, v]) =>
					v && searchParams.set(k, encodeURIComponent(JSON.stringify(v)))
			);

			return `${url}?${searchParams.toString()}`;
		}

		return url;
	}

	#formatHeaders(headers?: HttpHeaders): Record<string, string> {
		if (!headers) return this.getDefaults().headers;

		const headerMap = new Map(
			Object.entries(this.getDefaults().headers).map(([k, v]) => [
				k.toLowerCase(),
				v,
			])
		);

		Object.entries(headers).forEach(([k, v]) =>
			headerMap.set(k.toLowerCase(), v)
		);

		const MULTI_FORM_CONTENT_TYPE = "multipart/form-data";
		const CONTENT_HEADER_KEY = "content-type";

		/**
		 * Remove Content-Type from headers when for multipart/form-data for fetch
		 * Reference: https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post
		 */
		if (headerMap.get(CONTENT_HEADER_KEY) === MULTI_FORM_CONTENT_TYPE)
			headerMap.delete(CONTENT_HEADER_KEY);

		return Object.fromEntries(headerMap);
	}

	#formatBody(method: HttpMethod, data: unknown): BodyInit | null {
		if (["GET", "DELETE"].some(m => m === method)) return null;

		if (data instanceof FormData) return data;

		if (data instanceof File) return data;

		if (data instanceof Blob) return data;

		if (data instanceof ArrayBuffer) return data;

		if (ArrayBuffer.isView(data) && data.buffer instanceof ArrayBuffer)
			return data as ArrayBufferView<ArrayBuffer>;

		if (typeof data === "string") return data;

		if (Array.isArray(data) || (data && typeof data === "object"))
			return JSON.stringify(data);

		return null;
	}

	#isPositiveInteger(int: number) {
		return Number.isInteger(int) && int >= 0;
	}

	getDefaults() {
		return this.#defaults;
	}

	async #fetchWithTimeout(
		url: string | URL,
		{
			timeout,
			...config
		}: RequestInit & { timeout: number; method: HttpMethod }
	) {
		const controller = new AbortController();
		const signal = config.signal
			? AbortSignal.any([controller.signal, config.signal])
			: controller.signal;

		const fetchPromise = fetch(url, {
			...config,
			signal,
		});

		let timeoutId: NodeJS.Timeout | undefined;

		const timeoutPromise = new Promise<never>((_, reject) => {
			timeoutId = setTimeout(() => {
				reject({
					name: "RequestTimeoutError",
					statusCode: 408,
					message: `Request timed out after ${(timeout / 1000).toFixed(1)}s`,
				});
				controller.abort();
			}, timeout);
		});

		try {
			// Race the fetch against the timeout
			// Cast as Response as a value will only be resolved if the fetchPromise resolves first
			const res = (await Promise.race([
				fetchPromise,
				timeoutPromise,
			])) as Response;

			return res;
		} finally {
			// Clear the timeout to avoid memory leaks in case the fetch resolves before the timeout
			// as well as to prevent from aborting the connection on long-standing streams after the response has been received
			clearTimeout(timeoutId);
		}
	}

	#getErrorName(status: number): string {
		const errorNames = {
			400: "BadRequestError",
			401: "UnauthorizedError",
			403: "ForbiddenError",
			404: "NotFoundError",
			405: "MethodNotAllowedError",
			406: "NotAcceptableError",
			408: "RequestTimeoutError",
			409: "ConflictError",
			410: "GoneError",
			411: "LengthRequiredError",
			412: "PreconditionFailedError",
			413: "PayloadTooLargeError",
			414: "URITooLongError",
			415: "UnsupportedMediaTypeError",
			416: "RangeNotSatisfiableError",
			417: "ExpectationFailedError",
			418: "ImATeapotError",
			421: "MisdirectedRequestError",
			422: "UnprocessableEntityError",
			423: "LockedError",
			424: "FailedDependencyError",
			425: "TooEarlyError",
			426: "UpgradeRequiredError",
			428: "PreconditionRequiredError",
			429: "TooManyRequestsError",
			431: "RequestHeaderFieldsTooLargeError",
			451: "UnavailableForLegalReasonsError",
			500: "InternalServerError",
			501: "NotImplementedError",
			502: "BadGatewayError",
			503: "ServiceUnavailableError",
			504: "GatewayTimeoutError",
		} as const;

		return errorNames[status as keyof typeof errorNames] || "HttpError";
	}

	#getErrorDefaultMessage(status: number): string {
		const errorMessages = {
			400: "The request could not be understood by the server due to malformed syntax.",
			401: "The request requires user authentication.",
			403: "The server understood the request, but is refusing to fulfill it.",
			404: "The server has not found anything matching the Request-URI.",
			405: "The method specified in the Request-Line is not allowed for the resource identified by the Request-URI.",
			406: "The resource identified by the request is only capable of generating response entities which have content characteristics not acceptable according to the accept headers sent in the request.",
			408: "The server timed out waiting for the request.",
			409: "The request could not be completed due to a conflict with the current state of the resource.",
			410: "The requested resource is no longer available at the server and no forwarding address is known.",
			411: "The server requires a content-length in the request.",
			412: "The server does not meet one of the preconditions that the requester put on the request.",
			413: "The request is larger than the server is willing or able to process.",
			414: "The URI provided was too long for the server to process.",
			415: "The request entity has a media type which the server or resource does not support.",
			416: "The client has asked for a portion of the file, but the server cannot supply that portion.",
			417: "The server cannot meet the requirements of the Expect request-header field.",
			418: "I'm a teapot.",
			421: "The request was directed at a server that is not able to produce a response.",
			422: "The request was well-formed but was unable to be followed due to semantic errors.",
			423: "The resource that is being accessed is locked.",
			424: "The request failed due to failure of a previous request.",
			425: "The server is unwilling to risk processing a request that might be replayed.",
			426: "The server cannot process the request using the current protocol.",
			428: "The origin server requires the request to be conditional.",
			429: "The user has sent too many requests in a given amount of time.",
			431: "The server is unwilling to process the request because either an individual header field, or all the header fields collectively, are too large.",
			451: "The server is denying access to the resource as a consequence of a legal demand.",
			500: "The server encountered an unexpected condition which prevented it from fulfilling the request.",
			501: "The server does not support the functionality required to fulfill the request.",
			502: "The server, while acting as a gateway or proxy, received an invalid response from an inbound server it accessed while attempting to fulfill the request.",
			503: "The server is currently unable to handle the request due to a temporary overloading or maintenance of the server.",
			504: "The server, while acting as a gateway or proxy, did not receive a timely response from an upstream server it needed to access in order to complete the request.",
		} as const;
		return (
			errorMessages[status as keyof typeof errorMessages] ||
			"An error occurred while processing the request."
		);
	}

	#isHttpError(e: unknown): e is HttpError {
		if (!(e && typeof e === "object")) return false;

		if (!("message" in e && typeof e.message === "string")) return false;

		if (!("statusCode" in e && typeof e.statusCode === "number")) return false;

		return true;
	}

	#handleErrorResponse(status: number, message: string | object): HttpError {
		const error = this.#isHttpError(message)
			? message
			: {
					name: this.#getErrorName(status),
					statusCode: status,
					message:
						typeof message === "string"
							? message
							: this.#getErrorDefaultMessage(status),
				};
		return error;
	}

	async request<T = unknown>(
		path: string | URL,
		{
			method = "GET",
			data: originalBody,
			headers: newHeaders,
			timeout = this.getDefaults().timeout,
			retries = 0,
			params,
			signal,
			onStream,
			throwOnError = this.getDefaults().throwOnError,
			...rest
		}: HttpConfig & { method: HttpMethod }
	): Promise<T> {
		if (!this.getDefaults().baseURL && !path)
			throw new Error("Either the base URL or path needs to be defined");

		if (!this.#isPositiveInteger(retries))
			throw new Error("Retries must be positive integer");

		const url = this.#formatUrl(path, params);
		const headers = this.#formatHeaders(newHeaders);
		const body = this.#formatBody(method, originalBody);

		try {
			const res = await this.#fetchWithTimeout(url, {
				...rest,
				body,
				method,
				headers,
				timeout,
				signal,
			});

			if (res.status === 204)
				// T should be void when the client is expecting a 204 status with the successful case
				return undefined as T;

			if (res.body && onStream) {
				const decoder = new TextDecoder("utf-8");
				const reader = res.body.getReader();

				let done = false;

				while (!done) {
					const stream = await reader.read();

					done = stream.done;

					if (stream.value) {
						const chunk = decoder.decode(stream.value, { stream: true });

						onStream(chunk);
					}
				}

				return undefined as T;
			}

			const contentType = res.headers.get("content-type");

			if (!contentType) {
				if (res.body === null || (await res.text()).trim() === "") {
					return undefined as T; // No content to process
				}

				throw this.#handleErrorResponse(
					res.status,
					"Response must have a defined Content-Type when the response body is not empty"
				);
			}

			if (contentType?.includes("text/")) {
				const data = await res.text();

				if (!res.ok && throwOnError)
					throw this.#handleErrorResponse(res.status, data);

				// T should be string when the client is expecting a plain text response
				return data as T;
			} else if (contentType?.includes("image")) {
				const data = await res.blob();

				if (!res.ok && throwOnError)
					throw this.#handleErrorResponse(res.status, res.statusText);

				// T should be Blob when the client is expecting a image response
				return data as T;
			} else {
				const data = await res.json();

				if (!res.ok && throwOnError)
					throw this.#handleErrorResponse(res.status, data);

				return data;
			}
		} catch (e) {
			if (retries < 1) {
				if (e instanceof Error && e.name === "AbortError") {
					throw {
						name: "AbortError",
						statusCode: 0,
						message: e.message,
					};
				}
				if (e instanceof TypeError && e.message.includes("NetworkError")) {
					throw {
						name: "NetworkError",
						statusCode: 0,
						message: "Network error occurred",
					};
				}

				throw e;
			}

			return await this.request(path, {
				...rest,
				signal,
				method,
				data: body,
				headers,
				timeout,
				retries: retries - 1,
			});
		}
	}

	get<T>(url: string | URL, config: HttpConfig = {}) {
		return this.request<T>(url, {
			...config,
			method: "GET",
		});
	}

	post<T>(url: string | URL, data?: unknown, config: HttpConfig = {}) {
		return this.request<T>(url, {
			...config,
			method: "POST",
			data,
		});
	}

	put<T>(url: string | URL, data?: unknown, config: HttpConfig = {}) {
		return this.request<T>(url, {
			...config,
			method: "PUT",
			data,
		});
	}

	delete<T>(url: string | URL, config: HttpConfig = {}) {
		return this.request<T>(url, {
			...config,
			method: "DELETE",
		});
	}

	patch<T>(url: string | URL, data?: unknown, config: HttpConfig = {}) {
		return this.request<T>(url, {
			...config,
			method: "PATCH",
			data,
		});
	}

	async head(
		path: string | URL,
		{
			timeout = this.getDefaults().timeout,
			retries = 0,
			params,
			...config
		}: HttpConfig = {}
	): Promise<Headers> {
		if (!this.getDefaults().baseURL && !path)
			throw new Error("Either the base URL or path needs to be defined");

		if (!this.#isPositiveInteger(retries))
			throw new Error("Retries must be positive integer");

		const url = this.#formatUrl(path, params);
		const headers = this.#formatHeaders(config.headers);

		try {
			const res = await this.#fetchWithTimeout(url, {
				...config,
				method: "HEAD",
				timeout,
				headers,
			});

			if (!res.ok) {
				const data = await res.json();
				throw data;
			}

			return res.headers;
		} catch (e) {
			if (!retries) throw e;

			return await this.head(path, {
				...config,
				headers,
				timeout,
				retries: retries - 1,
			});
		}
	}
}
